import { useState } from 'react';
import { useNavigate } from 'react-router';
import { priceTableApi } from '@/api/commercial/price-tables';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

interface InactivatePriceTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceTableId?: string;
  onSuccess?: () => void;
}

export function InactivatePriceTableDialog({
  open,
  onOpenChange,
  priceTableId,
  onSuccess,
}: InactivatePriceTableDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleInactivate = async () => {
    if (!priceTableId) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'ID da tabela não encontrado.',
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await priceTableApi.inactivate(priceTableId);

      if (result.success) {
        toast({
          title: 'Sucesso',
          description: 'Tabela inativada com sucesso!',
        });
        onOpenChange(false);
        onSuccess?.();
        navigate('/commercial/price-table');
      } else {
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: result.error?.global || 'Erro ao inativar tabela. Tente novamente.',
        });
      }
    } catch (_error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao inativar tabela. Tente novamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Inativar tabela</DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Ao confirmar esta ação, a tabela será inativada. No caso de tabela do tipo normal será
            necessário aguardar o cadastro e publicação/ativação de nova tabela para que os preços
            voltem a aparecer nas propostas caso não tenha uma tabela provisória em vigor. No caso
            de tabela do tipo provisória, automaticamente a tabela do tipo normal que estiver 'Em
            Pausa' será reativada se houver. Caso não tenha tabela 'Em Pausa' então será necessário
            aguardar o cadastro e publicação/ativação de nova tabela para que os preços voltem a
            aparecer na proposta.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleInactivate} disabled={isLoading}>
            {isLoading ? 'Inativando...' : 'Inativar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
